import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import * as provider from '../../data/community/communityDataProvider';
import { useCommunityDataSource } from '../../utils/communityDataSource';
import ThreadPostCard from '../../components/CommunityThread/ThreadPostCard';
import ThreadDetailModal from '../../components/ThreadDetailModal/ThreadDetailModal';

const ACTING_USER_ID = 1001;

export default function CommunityNearby() {
  const [lat, setLat] = useState('37.4979');
  const [lon, setLon] = useState('127.0276');
  const [radius, setRadius] = useState('5000');
  const [planetId, setPlanetId] = useState('');
  const [submitted, setSubmitted] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [lastSeenAt] = useState(() =>
    new Date(Date.now() - 86400000).toISOString().replace(/\.\d{3}Z$/, 'Z'),
  );
  const dataSource = useCommunityDataSource();

  const nearbyQuery = useQuery({
    queryKey: ['communityNearby', dataSource, submitted],
    queryFn: () =>
      provider.getThreadsNearby(
        {
          latitude: submitted.lat,
          longitude: submitted.lon,
          radius_m: submitted.radius,
          planet_id: submitted.planetId || undefined,
          limit: 30,
        },
        ACTING_USER_ID,
      ),
    enabled: !!submitted,
  });

  const unreadQuery = useQuery({
    queryKey: ['communityNearbyUnread', dataSource, submitted],
    queryFn: () =>
      provider.postNearbyUnreadCount(
        {
          latitude: submitted.lat,
          longitude: submitted.lon,
          last_seen_at: lastSeenAt,
          planet_id: submitted.planetId || undefined,
        },
        ACTING_USER_ID,
      ),
    enabled: !!submitted,
  });

  const readDistMut = useMutation({
    mutationFn: (m) => provider.updateReadDistance({ read_distance_m: m }, ACTING_USER_ID),
  });

  const handleSearch = (e) => {
    e.preventDefault();
    setSubmitted({
      lat: Number(lat),
      lon: Number(lon),
      radius: Number(radius) || 1000,
      planetId,
    });
  };

  const rows = nearbyQuery.data?.data || [];
  const unread = unreadQuery.data;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-dark-border bg-dark-card/40 px-4 py-3 text-xs text-dark-text-secondary">
        Nearby search uses regional threads within radius. Mock mode applies read-distance settings
        (default 3000m for user 1001) and supports{' '}
        <code className="text-primary">post-nearby-unread-count</code>.
      </div>

      <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-dark-text-muted">Latitude</label>
          <input
            className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-36"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-dark-text-muted">Longitude</label>
          <input
            className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-36"
            value={lon}
            onChange={(e) => setLon(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-dark-text-muted">Radius (m)</label>
          <input
            className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-28"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-dark-text-muted">Planet ID</label>
          <input
            className="block mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm w-24"
            value={planetId}
            onChange={(e) => setPlanetId(e.target.value)}
          />
        </div>
        <button type="submit" className="px-4 py-2 bg-primary text-black rounded font-medium text-sm">
          Search nearby
        </button>
        <button
          type="button"
          className="px-3 py-2 border border-dark-border rounded text-sm hover:border-primary"
          onClick={() => readDistMut.mutate(Number(radius) || 5000)}
        >
          Set read distance
        </button>
      </form>

      {submitted && unread && (
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="px-3 py-1.5 rounded-full border border-primary/40 bg-primary/10 text-primary">
            Unread since {lastSeenAt.slice(0, 10)}: {unread.unread_count}
          </span>
          <span className="px-3 py-1.5 rounded-full border border-dark-border text-dark-text-muted">
            Effective radius: {unread.radius_m}m
          </span>
        </div>
      )}

      {nearbyQuery.isFetching && <p className="text-dark-text-secondary text-sm">Loading...</p>}

      <div className="max-w-xl mx-auto border border-dark-border rounded-xl overflow-hidden">
        {submitted && !nearbyQuery.isFetching && rows.length === 0 && (
          <p className="p-8 text-dark-text-muted text-sm text-center">No regional threads in range.</p>
        )}
        {rows.map((thread) => (
          <ThreadPostCard
            key={thread.id}
            thread={thread}
            actingUserId={ACTING_USER_ID}
            compact
            onAdminDetails={setSelectedId}
          />
        ))}
      </div>

      {selectedId && (
        <ThreadDetailModal
          threadId={selectedId}
          actingUserId={ACTING_USER_ID}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
